/** « Signer » : la liste des enveloppes, filtrable par colonne, avec
 *  pagination ; et les trois manieres d'en creer une. */
import { useEffect, useState } from "react";

import { api, liste, type EnveloppeResume, type Produit } from "../api";
import { Chargement, Erreur, Vide, date } from "../components/ui";

const PAR_PAGE = 20;

// Libelles du porteur (06/10/2026). Avancee et qualifiee : toujours
// visibles et toujours desactivees, « Bientôt disponible » (decision du
// porteur du 13/10/2026) ; la simple suit la disponibilite de son offre.
const NIVEAUX = [
  { code: "signature_simple", libelle: "Nouvelle signature eIDAS", bientot: false },
  { code: "signature_advanced", libelle: "Nouvelle signature eIDAS avancée", bientot: true },
  { code: "qes_external", libelle: "Nouvelle signature eIDAS qualifiée", bientot: true },
];

const ETATS: [string, string][] = [
  ["draft", "Brouillon"], ["prepared", "Avant signature"], ["sent", "Envoyée"], ["in_progress", "Signature partielle"],
  ["completed", "Signée"], ["declined", "Refusée"], ["cancelled", "Annulée"], ["expired", "Expirée"],
];

/** Porteur, 12/10/2026 : « toutes les enveloppes doivent avoir un état », avec
 *  les etats intermediaires (signature partielle), et « Bloquée » plutot
 *  qu'« Échec » pour un incident technique en cours. */
function etat(e: EnveloppeResume): { texte: string; classe: string; avancement: string } {
  const n = e.signer_count ?? 0;
  const k = e.signer_signed ?? 0;
  const ouverte = e.status === "sent" || e.status === "in_progress";
  const sur = `${k} sur ${n} signé${k > 1 ? "s" : ""}`;
  if (ouverte && e.outcome === "failure") {
    return { texte: "Bloquée", classe: "pill--err", avancement: e.outcome_reason || "Incident technique, reprise programmée" };
  }
  switch (e.status) {
    case "draft": return { texte: "Brouillon", classe: "pill--muted", avancement: "Pas encore envoyée" };
    case "prepared": return { texte: "Avant signature", classe: "pill--pending", avancement: "Remplissage ou validation en cours" };
    case "sent":
    case "in_progress":
      return k > 0
        ? { texte: `Signature partielle (${k}/${n})`, classe: "pill--warn", avancement: sur }
        : { texte: "Envoyée", classe: "pill--pending", avancement: sur };
    case "completed": return { texte: "Signée", classe: "pill--ok", avancement: `${n} sur ${n} signé${n > 1 ? "s" : ""}` };
    case "declined": return { texte: "Refusée", classe: "pill--err", avancement: e.outcome_reason || "Refusée par un signataire" };
    case "cancelled": return { texte: "Annulée", classe: "pill--muted", avancement: e.outcome_reason || "Annulée" };
    case "expired": return { texte: "Expirée", classe: "pill--muted", avancement: "Échéance dépassée" };
    default: return { texte: e.status, classe: "pill--muted", avancement: "—" };
  }
}

export function Envelopes({
  onOuvrir,
  onNouvelle,
}: {
  onOuvrir: (id: string) => void;
  onNouvelle: (produit?: string) => void;
}) {
  // Filtres par colonne (porteur, 06/10/2026), appliques par le serveur :
  // filtrer la seule page affichee laisserait croire a une absence.
  const [filtres, setFiltres] = useState<Record<string, string>>({});
  const [q, setQ] = useState("");
  const filtrer = (cle: string, valeur: string) => {
    setPage(0);
    setFiltres((f) => ({ ...f, [cle]: valeur }));
  };
  useEffect(() => {
    const t = setTimeout(() => filtrer("q", q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const [produits, setProduits] = useState<Produit[]>([]);
  useEffect(() => {
    void api.produits().then((r) => setProduits(liste<Produit>(r, "products")), () => undefined);
  }, []);
  // 15/10/2026 : l'avancée (clé d'accès) s'ouvre client par client dans l'Admin.
  const [avanceeOuverte, setAvanceeOuverte] = useState(false);
  useEffect(() => {
    void api.parametresClient().then((p) => setAvanceeOuverte(!!p.avancee_cle_acces), () => undefined);
  }, []);
  const [items, setItems] = useState<EnveloppeResume[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [erreur, setErreur] = useState<unknown>(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    let annule = false;
    setChargement(true);
    void (async () => {
      try {
        const r = await api.enveloppes(PAR_PAGE, page * PAR_PAGE, filtres);
        if (annule) return;
        setItems(r.envelopes ?? []);
        setTotal(r.total ?? 0);
      } catch (e) {
        if (!annule) setErreur(e);
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => {
      annule = true;
    };
  }, [page, filtres]);

  const pages = Math.max(1, Math.ceil(total / PAR_PAGE));

  return (
    <>
      <div className="actions actions--split" style={{ margin: "0 0 1rem" }}>
        <div>
          <h1 className="page-title">Enveloppes</h1>
          <p className="page-lead" style={{ margin: 0 }}>
            {total} enveloppe{total > 1 ? "s" : ""} au total.
          </p>
        </div>
        <div className="actions" style={{ margin: 0 }}>
          {NIVEAUX.map((n) => {
            const ouvert = !n.bientot || (n.code === "signature_advanced" && avanceeOuverte);
            const dispo = ouvert && produits.some((p) => p.code === n.code);
            return (
              <button key={n.code} type="button" className={ouvert ? "btn btn--primary" : "btn btn--secondary"} disabled={!dispo}
                      title={dispo ? undefined : "Bientôt disponible"} onClick={() => onNouvelle(n.code)}>
                {n.libelle}
              </button>
            );
          })}
        </div>
      </div>

      <Erreur erreur={erreur} />

      <div className="card">
        {chargement ? (
          <Chargement />
        ) : (
          <>
            <div className="table-wrap">
              <table className="table--liste">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>État</th>
                    <th>Avancement</th>
                    <th>Créée le</th>
                    <th>Échéance</th>
                    <th>Terminée le</th>
                    <th />
                  </tr>
                  <tr className="filtres">
                    <th>
                      <input className="input" placeholder="Rechercher…" value={q} aria-label="Filtrer par document"
                             onChange={(e) => setQ(e.target.value)} />
                    </th>
                    <th>
                      <select className="select" value={filtres.status ?? ""} aria-label="Filtrer par état"
                              onChange={(e) => filtrer("status", e.target.value)}>
                        <option value="">Tous</option>
                        {ETATS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                      </select>
                    </th>
                    <th>
                      <select className="select" value={filtres.outcome ?? ""} aria-label="Filtrer par résultat"
                              onChange={(e) => filtrer("outcome", e.target.value)}>
                        <option value="">Tous</option>
                        <option value="success">Succès</option>
                        <option value="failure">Échec</option>
                        <option value="aucun">Sans résultat</option>
                      </select>
                    </th>
                    <th>
                      <input className="input" type="date" value={filtres.cree_du ?? ""} aria-label="Créée à partir du"
                             onChange={(e) => filtrer("cree_du", e.target.value)} />
                      <input className="input" type="date" value={filtres.cree_au ?? ""} aria-label="Créée jusqu'au"
                             onChange={(e) => filtrer("cree_au", e.target.value)} />
                    </th>
                    <th />
                    <th>
                      <input className="input" type="date" value={filtres.termine_du ?? ""} aria-label="Terminée à partir du"
                             onChange={(e) => filtrer("termine_du", e.target.value)} />
                      <input className="input" type="date" value={filtres.termine_au ?? ""} aria-label="Terminée jusqu'au"
                             onChange={(e) => filtrer("termine_au", e.target.value)} />
                    </th>
                    <th>
                      {(q || Object.values(filtres).some(Boolean)) && (
                        <button type="button" className="btn btn--link"
                                onClick={() => { setQ(""); setFiltres({}); setPage(0); }}>
                          Effacer
                        </button>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan={7}><Vide>Aucune enveloppe.</Vide></td></tr>
                  )}
                  {items.map((e) => {
                    const et = etat(e);
                    return (
                    <tr key={e.id}>
                      <td>{e.title}</td>
                      <td>
                        <span className={`pill ${et.classe}`}>{et.texte}</span>
                      </td>
                      <td className="muted avancement" title={et.avancement}>{et.avancement}</td>
                      <td>{date(e.created_at, true)}</td>
                      <td>{date(e.expires_at, true)}</td>
                      <td>{date(e.completed_at, true)}</td>
                      <td className="num">
                        <button type="button" className="btn btn--link" onClick={() => onOuvrir(e.id)}>
                          Ouvrir
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Précédent
                </button>
                <span className="muted">
                  Page {page + 1} sur {pages}
                </span>
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={page + 1 >= pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
